import { List } from "immutable"
import * as React from "react"
import { Action, mk_widget, Widget } from "widgets-for-react"
import { ApiResult } from "../../../Shared/api/apiTypes"
import { hasValues, ready } from "../../../Shared/api/apiUtils"
import { LocalizationProps } from "../../../Shared/components/Jumbo/BranchLocalization/BranchLocalizationLayout"
import { ActivitySummaryProps } from "../../../Shared/components/LegacyProps"
import { ParsedParagraphOpeningHours } from "../../../Shared/drupal_models/types"
import { getActivities, getDatesForPeriod, Region } from "../../custom_api/api"
import { emptyOpt, makeOpt } from "../../custom_utils/option"
import { ActivitySummary } from "../components/activity_summary"
import { Block as BuildingBlock, BlockRenderer } from "../components/block_renderer"
import { contactDetails } from "../components/contact"
import { CookieUpdater } from "../components/cookiebar"
import { pushExpand, pushLocationEvent } from "../components/datalayer"
import { Jumbo } from "../components/jumbo"
import { LinkButton } from "../components/library/button"
import { Container } from "../components/library/container"
import { ExpandItem, ExpandProps } from "../components/library/expandItem"
import { Link } from "../components/library/link"
import { BasePageProps } from "../components/page_renderer"
import { HomePageProps } from "./home_page"

export interface LocationDetailPageDrupalProps extends BasePageProps {
  readonly kind: "locationDetail"
  readonly locationId: number
  readonly pageTitle: string
  readonly headerTitle: string
  readonly description: string
  readonly imageUrl: string
  readonly imageUrlLarge: string
  readonly phImageUrlSmall?: string
  readonly phImageUrl: string
  readonly phImageUrlLarge: string
  readonly imageAlt: string
  readonly currentTitle: string
  readonly currentContents: string
  readonly aboutTitle: string
  readonly aboutContents: string
  readonly openingHours: List<ParsedParagraphOpeningHours>
  readonly contactCustomerSupport: string
  readonly resetPeriod?: () => void
  readonly region: Region
  readonly blocks: List<BuildingBlock>
  readonly cookieUpdater: CookieUpdater
  readonly localizationProps?: LocalizationProps
}

export interface LocationDetailPageProps extends LocationDetailPageDrupalProps {
  readonly homePageData: ApiResult<HomePageProps>
}

export interface LocationDetailPageState {
  activitySummaries: ApiResult<List<ActivitySummaryProps>>,
  openingTimesOpen: boolean
  facilitiesOpen: boolean
  parkingOpen: boolean
  contactOpen: boolean
}

const locationDetailPageStateZero: LocationDetailPageState = {
  activitySummaries: "loading",
  openingTimesOpen: false,
  facilitiesOpen: false,
  parkingOpen: false,
  contactOpen: false
}

export class LocationDetailPage extends React.Component<LocationDetailPageProps, LocationDetailPageState> {
  constructor(props: LocationDetailPageProps) {
    super(props)
    pushLocationEvent({ location: props.headerTitle, region: props.region?.name})

    this.state = locationDetailPageStateZero
  }

  componentDidMount() {
    const {locationId, pageTitle} = this.props
    const s = this.state

    if (s.activitySummaries == "loading") {
      getActivities({
        cluster: emptyOpt(),
        edition: emptyOpt(),
        location: makeOpt(q => locationId ? q.Where(l => l.Id.Equals(locationId)) : q),
        activity: makeOpt(q => q.Where(a =>
          a.HideInOverview.Equals(false)
        )),
        region: emptyOpt(),
        event: makeOpt(q => q.Where(e => {
          let period = getDatesForPeriod('komend-halfjaar')

          return e.Date.IsAfter(period.start.toDate())
            .And(e.Date.IsBefore(period.end.toDate()))
        }))
      }).then(x => {
        this.setState((s, _) => ({ ...s, activitySummaries: x }))
      })
    }

    document.title = pageTitle
  }

  render() {
    const p = this.props
    const s = this.state

    return (
      <div className="location-page">
        <Jumbo
          kind="home-location"
          localizationProps={p.localizationProps}
          title={p.headerTitle}
          contents={`${p.description}`}
          imageUrl={p.phImageUrl}
          imageUrlLarge={p.phImageUrlLarge}
          imageAlt={p.imageAlt}
          size="small"
          openingHours={p.openingHours}
        />

        {ready(p.homePageData) && <BlockRenderer blocks={p.homePageData.universalBlocks}/>}

        {hasValues(s.activitySummaries) && (
          <section className="block block--activity-cluster container--lg align--center">
            <div className="block__inner">
              <header className="block__header container--m align--center">
                <h2 className="block__title title--l container--xs">Wat is hier te doen?</h2>
                <p className="block__intro container--s">In deze vestiging wordt van alles georganiseerd. Dit zijn de eerstvolgende activiteiten.</p>
              </header>

              <div className="block__content">
                <ul className="block__list row teasers">
                  {s.activitySummaries.take(6).map((s, i) => (
                    <ActivitySummary
                      key={`activity-summary-${i}`}
                      headingLevel="h3"
                      date={s.date}
                      imageAlt={s.imageAlt}
                      styleUrls={s.styleUrls}
                      location={s.location}
                      address={s.address}
                      route={s.route}
                      subtitle={s.subtitle}
                      title={s.title}
                      isTip={s.isTip}
                    />
                  ))}
                </ul>
              </div>

              <footer>
                <LinkButton
                  className="button button--primary button--arrow"
                  handle={p.resetPeriod}
                  route={`/activiteiten${p.region ? `?waar=${p.region.slug}` : ''}`}
                >
                  <span className="button__label">Bekijk alle activiteiten</span>
                </LinkButton>
                <Link className="button button--secondary" url="/terugblikken"><span className="button__label">Bekijk alle terugblikken</span></Link>
              </footer>
            </div>
          </section>
        )}

        <section className="block block--expand container--mg align--center">
          <Container>
            <header className="block__header">
              <h2 className="block__title title--l container--xs">Handige informatie</h2>
              <p className="block__intro container--s">Wil je weten wanneer je terecht kunt? En welke voorzieningen er op deze locatie zijn? Ontdek het hier.</p>
            </header>

            <div className="block__content">
              <div className="opening_hours_anchor" id="openingstijden"></div>
              <ul className="block__list expand__list">
                {Expand({
                  key: "openinghours",
                  open: s.openingTimesOpen,
                  title: "Openingstijden",
                  child: (
                    <>
                      <OpeningTimes openingHours={p.openingHours} />
                      <div dangerouslySetInnerHTML={{ __html: p.currentContents }} />
                    </>
                  )
                }).run(action => {
                  let openNew = action(s.openingTimesOpen)
                  if (openNew) { pushExpand('Openingstijden') }
                  this.setState({ ...s, openingTimesOpen: openNew })
                })}
                {Expand({
                  key: "facilities",
                  open: s.facilitiesOpen,
                  title: "Voorzieningen",
                  text: p.aboutContents
                }).run(action => {
                  let openNew = action(s.facilitiesOpen)
                  if (openNew) { pushExpand('Voorzieningen') }
                  this.setState({ ...s, facilitiesOpen: openNew })
                })}
                {Expand({
                  key: "contact",
                  open: s.contactOpen,
                  title: "Contact klantenservice",
                  child: contactDetails(p.contactCustomerSupport)
                }).run(action => {
                  let openNew = action(s.contactOpen)
                  if (openNew) { pushExpand('Contact') }
                  this.setState({ ...s, contactOpen: openNew })
                })}
              </ul>
            </div>
          </Container>
        </section>

        <BlockRenderer blocks={p.blocks} />
      </div>
    )
  }
}

const OpeningTimes: React.SFC<Pick<LocationDetailPageProps, "openingHours">> = props => {
  return (
    <div className="opening-hours__container">
      {props.openingHours
        .map(x => (
          <div key={`opening-hours-entry-${x.field_day}`} className="opening-hours__wrapper">
            <div className="opening-hours__info">
              <p className="opening-hours__day">{x.field_day + " "}</p>
              {x.field_closed == true ? (
                <span className="opening-hours__time">gesloten</span>
              ) : (
                  <time className="opening-hours__time">{x.field_opening_time + " - " + x.field_closing_time} uur</time>
                )}
            </div>
            {x.field_closed == true ? "" : <p className="opening-hours__text text--s">{x.field_additional_information}</p>}
          </div>
        ))
        .toArray()}
    </div>
  )
}

function Expand(
  props: ExpandProps,
): Widget<Action<boolean>> {
  return mk_widget({
    run: onDone => ExpandItem({ onClick: () => onDone(s => !s), ...props })
  })
}

{/* TH: Added to styleguide! */}
